/**
 * Vendedoras: ranking pelo valor fechado no periodo e o SLA de primeira
 * resposta (ate 15 min) de cada uma, com barra e percentual em texto.
 *
 * Cada cartao e tambem um atalho: "Ver a fila dela" recorta a fila ao lado
 * (e o mesmo filtro "vendedora" da lista), para a gestora ir do numero ao
 * atendimento em um clique. A comissao mostrada e a creditada ao toque do
 * motor; a vendedora aparece como quem fechou (ADR-001 E5).
 */

import { Link } from 'react-router-dom';
import { Badge, Button, Footnote, ProgressBar, SectionCard } from '../../../components/ui';
import { formatBRLFromCents, formatCount, formatPercentFromBps } from '../../../lib/format';
import { SLA_MINUTOS, tomDoSla, VENDEDORA_TODAS, type SlaDaVendedora } from '../fila';
import { prazoCurto } from '../vendaAssistida';

export function PainelDeVendedoras({
  linhas,
  slaDaEquipeBps,
  mediaDaEquipeMin,
  vendedoraAtiva,
  onFiltrar,
}: {
  readonly linhas: readonly SlaDaVendedora[];
  readonly slaDaEquipeBps: number | null;
  readonly mediaDaEquipeMin: number | null;
  readonly vendedoraAtiva: string;
  readonly onFiltrar: (vendedora: string) => void;
}): JSX.Element {
  return (
    <SectionCard
      id="vendedoras"
      title="Vendedoras"
      icon="usuarios"
      description={`Ranking pelo valor fechado no período e o SLA de primeira resposta (até ${SLA_MINUTOS} min).`}
      aside={
        <span title={`Equipe inteira: respostas em até ${SLA_MINUTOS} min sobre o total respondido no período.`}>
          <Badge tone={tomDoSla(slaDaEquipeBps)} dot>
            Equipe {slaDaEquipeBps === null ? '—' : formatPercentFromBps(slaDaEquipeBps)} no SLA
          </Badge>
        </span>
      }
    >
      {linhas.length === 0 ? (
        <p className="rounded-xl border border-dashed border-edge px-4 py-5 text-sm text-ink-subtle">
          Nenhuma vendedora cadastrada. Sem ninguém no comercial, quem responde à régua fica sem retorno — e a venda que viria dali some. Convide a equipe em Configurações → Equipe.
        </p>
      ) : (
        <ol className="flex flex-col gap-2" aria-label="Ranking das vendedoras pelo valor fechado">
          {linhas.map((l) => {
            const ativa = vendedoraAtiva === l.seller.id;
            const tom = tomDoSla(l.slaBps);
            return (
              <li key={l.seller.id} className={`flex flex-col gap-2 rounded-xl border px-3 py-2.5 text-sm transition-colors ${ativa ? 'border-brand/50 bg-brand/5' : 'border-line bg-elevated'}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="flex min-w-0 items-center gap-2">
                    <span className={`grid h-6 w-6 shrink-0 place-items-center rounded-full text-[11px] font-semibold tabular-nums ${l.posicao === 1 && l.valorFechadoCents > 0 ? 'bg-accent text-white' : 'bg-ink/5 text-ink-muted'}`}>
                      {l.posicao}º
                    </span>
                    <span className="truncate font-medium text-ink">{l.seller.name}</span>
                  </span>
                  <Badge tone={l.abertos > 3 ? 'warning' : 'neutral'}>
                    {formatCount(l.abertos)} {l.abertos === 1 ? 'aberto' : 'abertos'}
                  </Badge>
                </div>
                <p className="text-xs text-ink-muted">
                  <strong className="tabular-nums text-ink">{formatBRLFromCents(l.valorFechadoCents)}</strong> fechados · {formatCount(l.fechadas)} {l.fechadas === 1 ? 'venda' : 'vendas'}
                  {l.perdidas > 0 ? ` · ${formatCount(l.perdidas)} ${l.perdidas === 1 ? 'perdida' : 'perdidas'}` : ''} · comissão {formatBRLFromCents(l.comissaoCents)}
                </p>
                <div className="flex items-center gap-2" title={`${formatCount(l.noSla)} de ${formatCount(l.respondidos)} respostas em até ${SLA_MINUTOS} min.`}>
                  <ProgressBar value={l.noSla} max={l.respondidos} tone={tom} label={`SLA de primeira resposta de ${l.seller.name}`} className="flex-1" />
                  <span className="w-24 shrink-0 text-right text-xs tabular-nums text-ink-muted">{l.slaBps === null ? 'sem respostas' : `${formatPercentFromBps(l.slaBps)} no SLA`}</span>
                </div>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-[11px] text-ink-subtle">1ª resposta média {l.mediaMin === null ? '—' : prazoCurto(l.mediaMin)}</span>
                  <Button size="sm" variant="ghost" icon={ativa ? 'x' : 'filtro'} aria-pressed={ativa} onClick={() => onFiltrar(ativa ? VENDEDORA_TODAS : l.seller.id)}>
                    {ativa ? 'Mostrar todas' : 'Ver a fila dela'}
                  </Button>
                </div>
              </li>
            );
          })}
        </ol>
      )}
      <div className="mt-3 flex flex-col gap-2">
        <Footnote>
          Média da equipe na primeira resposta: {mediaDaEquipeMin === null ? '—' : prazoCurto(mediaDaEquipeMin)}. A comissão de cada venda é creditada ao toque da régua; a vendedora fica como quem fechou.
        </Footnote>
        <Link to={{ pathname: '/configuracoes', search: window.location.search }} className="text-sm font-medium text-brand hover:underline">
          Convidar vendedora em Configurações → Equipe
        </Link>
      </div>
    </SectionCard>
  );
}
